import { useEffect, useMemo, useState } from 'react';

import { curatorErrorCopy, type CuratorPrefillRow } from '../../api/curator-model';
import {
  fetchCuratorPrefillCandidates,
  useCuratorPrefillCreate,
  useCuratorPrefillDelete,
  useCuratorPrefillPatch,
} from '../../api/curator';
import { useCuratorGroupMemberAdd, useCuratorGroupMemberRemove, useCuratorGroups } from '../../api/curator-groups';
import { Combobox } from '../../components/Combobox';
import { Button, Drawer, Field, Input, Select } from '../../components/ui';
import { usePrograms } from '../quotas/dictionary';
import {
  FIO_HINT,
  FIO_PLACEHOLDER,
  PGU_ID_HINT,
  PREFILL_DICTIONARY_ERROR,
  PREFILL_FORMAT_OPTIONS,
  checkDraft,
  draftOfRow,
  groupAssignOptions,
  groupMembershipChange,
  groupSelectGate,
  groupSelectGateHint,
  patchOfDraft,
  prefillCourseSummary,
  prefillDraftFromFilters,
  prefillDictionaryState,
  prefillEditable,
  prefillProgramGroups,
  prefillProgramOptions,
  seedPrefillDraftProgram,
  type PrefillCourseSource,
  type PrefillDraft,
} from './cabinet-model';
import {
  classifyPrefillDuplicates,
  normalizeFioKey,
  type PrefillDuplicateWarn,
} from './prefill-duplicates';

/**
 * «Добавить ребёнка» / the row drawer (contract #26.3 and #26.5, SR §5.2), plus the
 * delete confirmation of #26.4.
 *
 * THE FIELD SET IS THE PROJECTION'S: ФИО, номер заявления, регион, УНТИ id,
 * программа. No contact field exists anywhere in the type — that is the server's
 * mask (A12/D9), not a hidden input.
 *
 * Validation mirrors the api rule for rule, so the curator sees «минимум два слова
 * буквами» before a round trip happens; the server stays the authority and its
 * `PREFILL_FIO_INVALID` (`meta.hint`) is surfaced verbatim when it answers anyway.
 *
 * T.Y18 (owner, live testing): a row OPENS this drawer — the inline «Править» /
 * «Удалить» buttons are gone. The drawer keeps editing to what the server allows
 * (`canEdit` — a matched row or a flag-off venue's row reads read-only), and carries
 * the destructive «Удалить» at its bottom for the rows the server says are
 * deletable (`canDelete`); the button only OPENS the confirmation drawer, which is
 * where the delete itself happens (Р15/D10 — the server's rules, unchanged).
 *
 * Z4 (owner, live testing): the editability gate is `!isEdit || canEdit`, not the
 * conjunction — a CREATE has no row, hence no server `canEdit`, and the old gate
 * rendered «Добавить ребёнка» as a form of disabled inputs behind a disabled button.
 *
 * T.Z24 (owner, live testing): submit runs the DUPLICATE GATE first — a scope-wide
 * `?q=` read, classified by {@link classifyPrefillDuplicates}. A hard block
 * (same ФИО + same номер заявления, or same ФИО + same регион) never reaches the
 * wire; a ФИО-only twin asks «Продолжить?» and only the «Продолжить» button sends
 * the write the curator already asked for. The gate's read is advisory by shape:
 * if it fails (offline, 403, anything) the write still goes and the server's own
 * 409 `CONFLICT` — the same hard-block copy (`curatorErrorCopy`) — stays the
 * backstop. An EDIT never warns against ITSELF: the row id is excluded.
 *
 * T.Z58 (owner, 14.09) — the programme fields are the OWNER'S DICTIONARY now, not a
 * course id: «Программа» is a Combobox over the programme PAIRS (the офл/онл twins
 * one short name names), «Аудитория» DERIVES from it and renders read-only, «Формат»
 * is a selectbox whose half of the pair completes the course. The «Курс» field is
 * GONE — the curator has no «курс», that was the internal concept; the resolved
 * course reads as the confirmation line under the fields instead. The body carries
 * the TRIPLE (`programName` + `audience` + `format`, contract v1.9.3) and the API
 * resolves the course — one resolution, the dictionary's own ladder. A programme the
 * curator typed into an older row is MATCHED fold-exact and shown as the selected
 * value; a MATCHED child shows OUR database's fields (the course decides), never the
 * curator's wording.
 *
 * T.Z76 (owner, 16.09) — the panel is the WIDE drawer (`.drawer--wide`, 1080px, the
 * import modal's own width): the two-column field grid's «Программа» Combobox did not
 * fit the default 560px and overflowed it. The delete confirmation below stays at the
 * default width — one sentence and two buttons do not need the room. The staff
 * manager's venue page (`/venues/:venueId/prefill`) renders THIS component, so both
 * surfaces widen together.
 */
export function PrefillFormDrawer({
  venueName,
  venueId,
  row,
  canEdit = row?.canEdit ?? true,
  filters,
  onClose,
  onSaved,
  onDelete,
}: {
  /** The display name of the scope the table is on — one venue, or «Все ваши площадки». */
  venueName: string;
  /** The create target (#26.3). Present whenever the add form can open — the add
   * button only exists on a venue view, because the venue decides the partner pool. */
  venueId?: number;
  /** `undefined` = the add form. */
  row?: CuratorPrefillRow;
  /** Server-computed editability (`canEdit` of #26.2). `false` renders a read-only drawer. */
  canEdit?: boolean;
  /** T.Z58 — the working table's active programme/audience filters, which PREFILL a
   * new-child form (a new row inherits the slice the curator was looking at). */
  filters?: { courseId?: number; audience?: string };
  onClose: () => void;
  onSaved: (message: string) => void;
  /** Opens the delete confirmation; rendered only when the server allows the delete. */
  onDelete?: (row: CuratorPrefillRow) => void;
}) {
  const isEdit = row !== undefined;
  // Z4 — a CREATE has no row and no server `canEdit` to respect, so it is editable
  // by shape; only an EDIT defers to the server. (`isEdit && canEdit` here made
  // «Добавить ребёнка» a drawer of disabled inputs.)
  const editable = prefillEditable(isEdit, canEdit);
  const programs = usePrograms();
  // T.Z64 — the read's own verdict, not the array length: a FAILED read is `error`
  // (rendered loudly below), a successful-but-empty one stays `ready`. Folding a
  // failure into `[]` is what made a 403 look like «Программа не найдена».
  const dictionaryState = prefillDictionaryState(programs);
  const dictionary: PrefillCourseSource[] = programs.data?.data ?? [];
  const [draft, setDraft] = useState<PrefillDraft>(() =>
    row === undefined
      ? // T.Z58 — a new child inherits the table's active programme/audience filters.
        prefillDraftFromFilters(filters, dictionary)
      : draftOfRow(row, dictionary),
  );
  const [formError, setFormError] = useState<string | null>(null);
  // T.Z24 — the parked warning. `null` = the form is not asking anything; a
  // non-null value is the verdict the curator has yet to answer with
  // «Продолжить»/«Отменить».
  const [warning, setWarning] = useState<PrefillDuplicateWarn | null>(null);
  const [checking, setChecking] = useState(false);
  const create = useCuratorPrefillCreate();
  const patch = useCuratorPrefillPatch();

  // ADDITIVE (v1.9.6, T.Z62) — «Группа». The select speaks #26.12/#26.13 over the
  // row's MATCHED application, so its gate is the honest one: a row without an
  // application has nothing to assign, and the «Все места» drawer does not know the
  // row's venue (the row payload carries no venueId), so it cannot fetch THAT venue's
  // group list — both cases disable the select with the way in named, never a silent
  // dead control. A CREATE is always unmatched by definition.
  const groupGate = row === undefined ? 'unmatched' : groupSelectGate({ applicationId: row.applicationId, hasVenue: venueId !== undefined });
  const groups = useCuratorGroups(groupGate === 'ok' && venueId !== undefined ? venueId : null);
  const groupOptions = useMemo(() => groupAssignOptions(groups.data ?? []), [groups.data]);
  const [groupDraft, setGroupDraft] = useState<string>(() => (row?.group ?? null) === null ? '' : String(row?.group?.id ?? ''));
  const addMember = useCuratorGroupMemberAdd();
  const removeMember = useCuratorGroupMemberRemove();
  const groupChange = useMemo(
    // The write the current draft asks for, folded once (`cabinet-model`): a move is
    // BOTH calls — remove from the old group, then assign the new one.
    () => (row === undefined ? null : groupMembershipChange(row.group, groupDraft === '' ? null : Number(groupDraft))),
    [row, groupDraft],
  );

  // The programme/format pair is required for a NEW row (the owner's rule, carried
  // over from the «Курс» era); an edit may leave the row course-less.
  const check = useMemo(
    () => checkDraft(draft, { programs: dictionary, requireCourse: row === undefined }),
    [draft, dictionary, row],
  );
  const busy = create.isPending || patch.isPending || checking || addMember.isPending || removeMember.isPending;

  // T.Z58 — the drawer's programme list: one option per PAIR. An active «Аудитория»
  // filter (with no «Программа» filter) narrows the list to that slice — the table's
  // filter prefilling the field's CHOICES, since the audience itself derives.
  const programOptions = useMemo(
    () => prefillProgramOptions(dictionary, filters?.courseId === undefined ? filters?.audience : undefined),
    [dictionary, filters?.audience, filters?.courseId],
  );
  const programGroups = useMemo(() => prefillProgramGroups(dictionary), [dictionary]);
  const chosenGroup = programGroups.find((group) => group.key === draft.programKey) ?? null;
  const unmatchedText = draft.programKey === '' ? draft.programName.trim() : null;
  const courseSummary = useMemo(() => prefillCourseSummary(draft, dictionary), [draft, dictionary]);

  // The dictionary read is cached by the page (the table's «Программа» filter uses the
  // same query), so `dictionary` is usually populated BEFORE the drawer mounts. It does
  // not have to be — a cold deep link (or this very page, whose first render races the
  // curator's first keystroke) can open the form over a query still in flight — so the
  // programme seeding re-runs once the dictionary first arrives. It MERGES, never
  // replaces: a replace threw away everything typed before the read landed (the
  // e2e-auth harness caught the ФИО vanishing and the submit gate closing with it),
  // and a curator typing on a slow dictionary would hit the same loss.
  const [seeded, setSeeded] = useState(dictionary.length > 0);
  useEffect(() => {
    if (seeded || dictionary.length === 0) return;
    setSeeded(true);
    setDraft((current) =>
      seedPrefillDraftProgram(
        current,
        row === undefined ? prefillDraftFromFilters(filters, dictionary) : draftOfRow(row, dictionary),
      ),
    );
  }, [dictionary, seeded, row, filters]);

  // A warning speaks about the draft that produced it. One keystroke later it is
  // about a child the curator is no longer typing — so it goes, and the next
  // submit re-runs the gate on the new values. (React bails out of the mount run.)
  useEffect(() => setWarning(null), [draft]);

  /**
   * ADDITIVE (v1.9.6, T.Z62) — the group half of the save. A no-op change sends
   * nothing; a move is remove-then-assign (the api has no «move»); the notice
   * collects what actually happened, so the curator reads one sentence about the row
   * and, when it moved, one about the group.
   */
  const applyGroupChange = (report: (parts: string[]) => void) => {
    if (groupChange === null || row === undefined || row.applicationId === null) {
      report([]);
      return;
    }
    const applicationId = row.applicationId;
    if (groupChange.removeFrom !== undefined) {
      removeMember.mutate(
        { groupId: groupChange.removeFrom, applicationId },
        {
          onSuccess: () => {
            if (groupChange.assignTo === undefined) {
              report(['Группа убрана.']);
              return;
            }
            addMember.mutate(
              { groupId: groupChange.assignTo, applicationId },
              {
                onSuccess: (group) => report([`Группа: «${group.name}».`]),
                onError: (cause) => setFormError(copyOf(cause)),
              },
            );
          },
          onError: (cause) => setFormError(copyOf(cause)),
        },
      );
      return;
    }
    if (groupChange.assignTo !== undefined) {
      addMember.mutate(
        { groupId: groupChange.assignTo, applicationId },
        {
          onSuccess: (group) => report([`Группа: «${group.name}».`]),
          onError: (cause) => setFormError(copyOf(cause)),
        },
      );
    }
  };

  /** The write the form currently holds — recomputed at the click, never cached. */
  const send = () => {
    if (row === undefined) {
      if (venueId === undefined) {
        // Unreachable from the UI (the add button only exists on a venue view), but
        // the create without a venue has no answer to send — so it never goes.
        setFormError('Сначала выберите площадку.');
        return;
      }
      create.mutate(
        { venueId, ...check.patch },
        {
          onSuccess: () => onSaved('Строка добавлена. Ребёнок будет сопоставлен при следующей проверке.'),
          onError: (error) => setFormError(copyOf(error)),
        },
      );
      return;
    }

    const body = patchOfDraft(draft, row, dictionary);
    const patched = Object.keys(body).length > 0;
    if (!patched && groupChange === null) {
      onSaved('Изменений нет.');
      return;
    }
    if (!patched) {
      applyGroupChange((parts) => onSaved(parts.join(' ') || 'Изменений нет.'));
      return;
    }
    patch.mutate(
      { rowId: row.id, patch: body },
      {
        onSuccess: () => {
          const rowParts = ['Строка обновлена. Она снова пойдёт через проверку.'];
          applyGroupChange((groupParts) => onSaved([...rowParts, ...groupParts].join(' ')));
        },
        onError: (error) => setFormError(copyOf(error)),
      },
    );
  };

  /**
   * The gate, then the write. Anything it cannot decide (a failed read) falls
   * THROUGH to the write — the server is still the authority, and its 409 renders
   * as the same block.
   */
  const gateThenSend = async () => {
    setChecking(true);
    try {
      const hits = await fetchCuratorPrefillCandidates(normalizeFioKey(check.patch.fullName));
      const verdict = classifyPrefillDuplicates(
        { fullName: check.patch.fullName, pguId: check.patch.pguId, region: check.patch.region },
        hits,
        // The row being edited is in the very scope the read just answered — it is
        // not its own duplicate. A CREATE has no id to exclude.
        { excludeRowId: row?.id },
      );
      if (verdict.kind === 'block') {
        setFormError(verdict.message);
        return;
      }
      if (verdict.kind === 'warn') {
        setWarning(verdict);
        return;
      }
    } catch {
      // Advisory read failed — the server's 409 is the backstop, so submit anyway.
    } finally {
      setChecking(false);
    }
    send();
  };

  const submit = () => {
    setFormError(null);
    setWarning(null);

    // The row write and the group write are judged SEPARATELY (T.Z62): a read-only
    // row (matched, Р15) still saves a group change, and that save runs neither the
    // ФИО gate (the duplicate check exists for a row edit, not a membership move) nor
    // the row's own submit validation — there is no row body in it.
    if (row !== undefined && Object.keys(patchOfDraft(draft, row, dictionary)).length === 0) {
      if (groupChange === null) {
        onSaved('Изменений нет.');
        return;
      }
      applyGroupChange((parts) => onSaved(parts.join(' ') || 'Изменений нет.'));
      return;
    }

    if (!check.canSubmit) return;
    void gateThenSend();
  };

  return (
    <Drawer
      open
      wide
      onClose={onClose}
      title={isEdit ? (editable ? 'Править строку' : 'Строка') : 'Добавить ребёнка'}
      description={
        isEdit
          ? `Площадка «${venueName}». ${editable ? 'Строку можно править, пока ребёнок не сопоставлен.' : 'Строку нельзя изменить: она уже сопоставлена или ведётся на площадке без внесения.'}`
          : `Площадка «${venueName}». Ребёнок будет сопоставлен автоматически по номеру заявления или ФИО.`
      }
      footer={
        <>
          {isEdit ? (
            <Button variant="ghost" data-testid="prefill-cancel" disabled={busy} onClick={onClose}>
              {/* T.Z62 — a matched row with a live «Группа» select is a form again in
                  that one field, so «Отмена» is honest even though the fields are not. */}
              {editable || groupGate === 'ok' ? 'Отмена' : 'Закрыть'}
            </Button>
          ) : null}
          {editable || (isEdit && groupGate === 'ok') ? (
            <>
              {isEdit && editable && row.canDelete && onDelete !== undefined ? (
                <Button
                  variant="danger"
                  size="sm"
                  data-testid="prefill-drawer-delete"
                  onClick={() => onDelete(row)}
                >
                  Удалить
                </Button>
              ) : null}
              <Button
                variant="primary"
                data-testid="prefill-submit"
                loading={busy}
                disabled={editable && !check.canSubmit}
                onClick={submit}
              >
                {isEdit ? 'Сохранить' : 'Добавить'}
              </Button>
            </>
          ) : null}
          {/* The create footer lives outside the `editable` branch so the two can
              never double-render the same `prefill-submit` (Z4 folded them apart:
              a create IS editable now, and the branches would overlap). */}
          {row === undefined ? (
            <Button variant="ghost" data-testid="prefill-cancel" disabled={busy} onClick={onClose}>
              Отмена
            </Button>
          ) : null}
        </>
      }
    >
      <div className="form-grid">
        <div className="form-grid__full">
          <Field
            label="ФИО ребёнка"
            htmlFor="prefill-fio"
            required={editable}
            hint={FIO_HINT}
            error={editable ? check.errors.fullName : undefined}
          >
            <Input
              id="prefill-fio"
              data-testid="prefill-fio"
              placeholder={FIO_PLACEHOLDER}
              value={draft.fullName}
              disabled={busy || !editable}
              onChange={(event) => setDraft({ ...draft, fullName: event.target.value })}
            />
          </Field>
        </div>

        <Field
          label="Номер заявления (ЕПГУ)"
          htmlFor="prefill-pgu"
          hint={PGU_ID_HINT}
          error={editable ? check.errors.pguId : undefined}
        >
          <Input
            id="prefill-pgu"
            data-testid="prefill-pgu"
            className="input--urged"
            inputMode="numeric"
            maxLength={10}
            placeholder="10 цифр"
            value={draft.pguId}
            disabled={busy || !editable}
            aria-describedby="prefill-pgu-hint"
            onChange={(event) => setDraft({ ...draft, pguId: event.target.value.replace(/\D/g, '').slice(0, 10) })}
          />
        </Field>

        <Field
          label="УНТИ ID"
          htmlFor="prefill-unti"
          hint="Если известен — до 12 цифр."
          error={editable ? check.errors.untiId : undefined}
        >
          <Input
            id="prefill-unti"
            data-testid="prefill-unti"
            inputMode="numeric"
            maxLength={12}
            value={draft.untiId}
            disabled={busy || !editable}
            onChange={(event) => setDraft({ ...draft, untiId: event.target.value.replace(/\D/g, '').slice(0, 12) })}
          />
        </Field>

        <Field label="Регион или город" htmlFor="prefill-region" hint="Как написано у партнёра — регион нормализуется на сервере.">
          <Input
            id="prefill-region"
            data-testid="prefill-region"
            placeholder="Например, Кемерово"
            value={draft.region}
            disabled={busy || !editable}
            onChange={(event) => setDraft({ ...draft, region: event.target.value })}
          />
        </Field>

        {/* T.Z58 — «Программа»: a Combobox over the DICTIONARY's programme pairs, the
            field the owner asked for instead of «Курс». Choosing it auto-fills the
            audience below; the free text a curator typed into an older row is matched
            fold-exact and shown as the selected value.
            T.Z64 — a FAILED dictionary read renders the error with a retry instead of
            the combobox: «Программа не найдена» over zero options told the curator
            «нет такой программы» when the truth was «словарь не пришёл» (the api's 403
            on #21 before v1.9.6). */}
        <Field
          label="Программа"
          htmlFor="prefill-program"
          hint="Из словаря программ; аудитория заполнится сама."
          error={editable ? check.errors.programKey : undefined}
        >
          {dictionaryState.kind === 'error' ? (
            <div className="cabinet-duplicate" role="alert" data-testid="prefill-program-error">
              <p className="cabinet-duplicate__text">{PREFILL_DICTIONARY_ERROR}</p>
              <div className="cabinet-duplicate__actions">
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="prefill-program-retry"
                  loading={programs.isFetching}
                  onClick={() => void programs.refetch()}
                >
                  Повторить
                </Button>
              </div>
            </div>
          ) : (
            <Combobox
              id="prefill-program"
              testId="prefill-program"
              value={draft.programKey}
              options={programOptions}
              onChange={(next) => {
                // A fresh choice keeps the format only if the new programme still runs
                // it — otherwise the pair would silently name nothing.
                setDraft((current) => {
                  const group = programGroups.find((candidate) => candidate.key === next);
                  const format = current.format.trim() as 'OFFLINE' | 'ONLINE' | '';
                  const keepFormat = next !== '' && format !== '' && group?.byFormat[format] !== undefined;
                  return { ...current, programKey: next, format: keepFormat ? current.format : '' };
                });
              }}
              placeholder="Выберите программу"
              emptyText="Программа не найдена"
              clearLabel="Убрать программу"
              ariaLabel="Программа"
              disabled={busy || !editable}
              resultsLabel={(count) => `Найдено программ: ${count}`}
            />
          )}
          {unmatchedText !== null ? (
            <p className="cabinet-form__hint" data-testid="prefill-program-unmatched">
              В строке записано: «{unmatchedText}» — такой программы в словаре нет, текст сохранится как есть.
            </p>
          ) : null}
        </Field>

        {/* T.Z58 — «Аудитория»: NOT an input. It fills itself from the chosen programme
            (the dictionary's audience) — the owner's «заполняется автоматически», and
            for a MATCHED row it is OUR database speaking, not the curator's text. */}
        <Field label="Аудитория" htmlFor="prefill-audience" hint="Заполняется автоматически из программы.">
          <Input
            id="prefill-audience"
            data-testid="prefill-audience"
            value={chosenGroup?.audienceLabel ?? ''}
            placeholder="—"
            readOnly
            disabled
          />
        </Field>

        {/* T.Z58 — «Формат»: the half of the офл/онл pair. Together with the programme
            it names ONE course of the dictionary; the course reads below. */}
        <Field
          label="Формат"
          htmlFor="prefill-format"
          hint="Онлайн или офлайн — вместе с программой определяет курс."
          error={editable ? check.errors.format : undefined}
        >
          <Select
            id="prefill-format"
            data-testid="prefill-format"
            value={draft.format}
            disabled={busy || !editable}
            onChange={(event) => setDraft({ ...draft, format: event.target.value })}
          >
            <option value="">— выберите —</option>
            {PREFILL_FORMAT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          {courseSummary !== null ? (
            <p className="cabinet-form__hint" data-testid="prefill-course-summary">
              Курс: {courseSummary}
            </p>
          ) : null}
        </Field>

        {/* ADDITIVE (v1.9.6, T.Z62) — «Группа»: the учебные группы of the row's venue
            (#26.8), assigned and removed through #26.12/#26.13 by the matched child's
            `applicationId`. The select survives a READ-ONLY row on purpose: Р15 freezes
            the row's fields after the match, but the group membership is its own
            surface — assigning is exactly what a matched row is for. A row the matcher
            has not found yet has no application to assign, and the «Все места» drawer
            does not know the row's venue; both states disable the select with the way
            in named (see {@link groupSelectGateHint}). */}
        <Field label="Группа" htmlFor="prefill-group-select" hint="Учебные группы этой площадки — до 30 учеников в каждой.">
          <Select
            id="prefill-group-select"
            data-testid="prefill-group-select"
            value={groupDraft}
            disabled={busy || groupGate !== 'ok' || groups.isPending}
            onChange={(event) => setGroupDraft(event.target.value)}
          >
            {groupOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          {groupSelectGateHint(groupGate) !== null ? (
            <p className="cabinet-form__hint" data-testid="prefill-group-hint">
              {groupSelectGateHint(groupGate)}
            </p>
          ) : null}
        </Field>
      </div>

      {warning !== null ? (
        /* T.Z24 — the ФИО-only twin. It asks, it does not forbid: «Продолжить»
            sends the write the curator already asked for, «Отменить» keeps the
            drawer open with the values intact so the ФИО can be fixed instead. */
        <div className="cabinet-duplicate" role="alert" data-testid="prefill-duplicate-warning">
          <p className="cabinet-duplicate__text">{warning.message}</p>
          <div className="cabinet-duplicate__actions">
            <Button
              variant="secondary"
              size="sm"
              data-testid="prefill-duplicate-continue"
              disabled={busy}
              onClick={() => {
                setWarning(null);
                send();
              }}
            >
              Продолжить
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="prefill-duplicate-cancel"
              disabled={busy}
              onClick={() => setWarning(null)}
            >
              Отменить
            </Button>
          </div>
        </div>
      ) : null}

      {formError !== null ? (
        <p className="field__error" role="alert" data-testid="prefill-form-error">
          {formError}
        </p>
      ) : null}

      {editable ? (
        <p className="cabinet-note" data-testid="prefill-form-note">
          Номер заявления — основной ключ поиска: с ним ребёнок находится даже при расхождении в ФИО.
          Строку можно править, пока она не сопоставлена.
        </p>
      ) : (
        <p className="cabinet-note" data-testid="prefill-form-note">
          {groupGate === 'ok'
            ? 'Строку нельзя изменить: она уже сопоставлена. Назначить или сменить учебную группу можно.'
            : 'Строку нельзя изменить: она уже сопоставлена или закреплена за площадкой, где внесение закрыто. Правки — через администратора.'}
        </p>
      )}
    </Drawer>
  );
}

/**
 * «Удалить строку?» — the confirmation drawer of #26.4, only for own NOT_FOUND rows.
 *
 * T.Y18: the drawer says the delete is SOFT in so many words — «строка останется в
 * истории» — because «исчезает» alone read to the owner as «данные стираются», and
 * the 0017 CHECK (`deleted_at IS NULL OR status = 'NOT_FOUND'`) plus the audit row
 * mean nothing is destroyed.
 */
export function PrefillDeleteDrawer({
  venueName,
  row,
  onClose,
  onDeleted,
}: {
  venueName: string;
  row: CuratorPrefillRow;
  onClose: () => void;
  onDeleted: (message: string) => void;
}) {
  const remove = useCuratorPrefillDelete();
  const [error, setError] = useState<string | null>(null);

  const confirm = () => {
    setError(null);
    remove.mutate(row.id, {
      onSuccess: () => onDeleted('Строка удалена.'),
      onError: (requestError) => setError(copyOf(requestError)),
    });
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Удалить строку?"
      description={`Площадка «${venueName}». Удаление мягкое: строка исчезает из кабинета и реестра.`}
      footer={
        <>
          <Button variant="danger" data-testid="prefill-delete-confirm" loading={remove.isPending} onClick={confirm}>
            Удалить
          </Button>
          <Button variant="ghost" data-testid="prefill-delete-cancel" disabled={remove.isPending} onClick={onClose}>
            Отмена
          </Button>
        </>
      }
    >
      <p className="cabinet-note">
        {row.fullName}
        {row.region !== null ? `, ${row.region}` : ''}
        {row.pguId !== null ? `, заявление ${row.pguId}` : ''}. Удалить можно только строку «не найдено», пока
        ребёнок не сопоставлен.
      </p>
      {/* The soft-delete promise (T.Y18): the row leaves the screen, not the system. */}
      <p className="cabinet-note" data-testid="prefill-delete-hint">
        Строка останется в истории — данные не стираются, и администратор увидит её в журнале.
      </p>
      {error !== null ? (
        <p className="field__error" role="alert" data-testid="prefill-delete-error">
          {error}
        </p>
      ) : null}
    </Drawer>
  );
}

/** The api's refusal, verbatim in wording (its `meta.hint`/`meta.limit` included). */
function copyOf(error: unknown): string {
  return curatorErrorCopy(error);
}
